import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createHead } from '@vueuse/head'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import App from './App.vue'
import router from './router'
import { createI18nInstance, setI18nInstance } from './i18n'
import { useAppStore } from './stores/app'
import { scheduleAnalyticsLoad } from './utils/analytics'
import { clearVitePreloadRecovery, installVitePreloadRecovery } from './utils/vitePreloadRecovery'

// Import global styles
import './style.css'

installVitePreloadRecovery()

// 異步初始化應用
async function initApp() {
  const app = createApp(App)

  // Pinia store
  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)

  // Head management
  const head = createHead()

  // Toast notifications configuration
  const toastOptions = {
    timeout: 3000,
    maxToasts: 4,
    newestOnTop: true,
    closeOnClick: true,
    pauseOnFocusLoss: true,
    pauseOnHover: true,
    draggable: true,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: false,
    closeButton: 'button',
    icon: true,
    rtl: false,
    transition: 'Vue-Toastification__fade',
    toastClassName: 'sugarm-toast',
    bodyClassName: 'sugarm-toast-body',
    toastDefaults: {
      success: { timeout: 2500 },
      info: { timeout: 2500 },
      warning: { timeout: 4000 },
      error: { timeout: 6500 },
    },
    position: 'top-right' as const,
  }

  try {
    // 初始化 i18n
    const i18n = await createI18nInstance()
    
    app.use(pinia)
    app.use(router)
    app.use(i18n)
    app.use(head)
    app.use(Toast, toastOptions)

    // Set i18n instance for global access
    setI18nInstance(i18n)

    // Initialize app store after pinia
    const appStore = useAppStore()
    appStore.initializeTheme()

    app.mount('#app')
    clearVitePreloadRecovery()
    scheduleAnalyticsLoad()
    console.log('🌍 i18n with YAML loaded successfully')
  } catch (error) {
    console.error('Failed to initialize i18n:', error)
    // 如果失敗，仍然掛載應用但不使用 i18n
    app.use(pinia)
    app.use(router)
    app.use(head)
    app.use(Toast, toastOptions)
    app.mount('#app')
    clearVitePreloadRecovery()
    scheduleAnalyticsLoad()
  }
}

// 啟動應用
initApp()
